import React, { useState, useCallback, useEffect, useRef } from 'react';
import { loadNotifPrefs, saveNotifPrefs, sendAthenaNotification as sendWebAthenaNotification } from '../utils/athenaNotifications';
import { API_BASE, IS_NATIVE_APP } from '../config/api';
import {
  getAthenaNotificationStatus,
  registerAthenaNotifications,
  requestAthenaNotificationsPermission,
  testAthenaNotifications,
} from '../notifications/AthenaNotifications';
import { useAthenaDeviceContext } from '../context/AthenaDeviceContextProvider';
import { useAthenaRemoteConfig } from '../config/RemoteConfigProvider';
import {
  disableAthenaFloatingOverlay,
  enableAthenaFloatingOverlay,
  getAthenaFloatingOverlayStatus,
  requestAthenaFloatingOverlayPermission,
  setAthenaFloatingOverlayInteractionLevel,
} from '../platform/AthenaFloatingOverlay';

// ── Design tokens ──────────────────────────────────────────────────────────────
const P = {
  bg:          'rgba(8,12,28,0.30)',
  card:        'rgba(255,255,255,0.04)',
  cardHov:     'rgba(255,255,255,0.07)',
  cardHi:      'rgba(59,130,246,0.08)',
  cardHiHov:   'rgba(59,130,246,0.13)',
  border:      'rgba(255,255,255,0.07)',
  borderHi:    'rgba(59,130,246,0.18)',
  accent:      '#3B82F6',
  accentMuted: 'rgba(59,130,246,0.65)',
  text:        '#e8eaf2',
  muted:       'rgba(255,255,255,0.42)',
  dim:         'rgba(255,255,255,0.22)',
  red:         '#EF4444',
  redCard:     'rgba(239,68,68,0.06)',
  redBorder:   'rgba(239,68,68,0.18)',
};

const KF = `
@keyframes panelIn { from { opacity:0; transform:translateY(28px) scale(0.97); } to { opacity:1; transform:none; } }
@keyframes bdIn    { from { opacity:0; } to { opacity:1; } }
@keyframes toastIn { from { opacity:0; transform:translateX(-50%) translateY(8px); } to { opacity:1; transform:translateX(-50%); } }
@keyframes spin    { to { transform: rotate(360deg); } }
`;

// ── Apply preferences globally ─────────────────────────────────────────────────
export function applyPreferences(prefs) {
  if (!prefs) return;
  const fontMap = {
    'Inter':           'Inter, sans-serif',
    'Outfit':          '"Outfit", sans-serif',
    'Instrument Sans': '"Instrument Sans", sans-serif',
    'Serif':           'Georgia, "Times New Roman", serif',
  };
  const zoomMap = { 'Pequena': '0.9', 'Normal': '1', 'Grande': '1.15' };
  const animMap = { 'Ativas': 'active', 'Reduzidas': 'reduced', 'Desativadas': 'none' };
  const intMap  = { 'Minimalista': 'minimal', 'Equilibrado': 'balanced', 'Imersivo': 'immersive' };

  if (prefs.fonte)        document.body.style.fontFamily = fontMap[prefs.fonte] ?? 'Inter, sans-serif';
  if (prefs.tamanhoFonte) document.documentElement.style.zoom = zoomMap[prefs.tamanhoFonte] ?? '1';
  if (prefs.animacoes)    document.documentElement.setAttribute('data-anim',      animMap[prefs.animacoes]   ?? 'active');
  if (prefs.intensidade)  document.documentElement.setAttribute('data-intensity', intMap[prefs.intensidade]  ?? 'balanced');
  if (prefs.compacto != null) document.documentElement.setAttribute('data-compact', prefs.compacto ? 'true' : 'false');

  window.dispatchEvent(new CustomEvent('athena-prefs-changed'));
}

// ── Utility ────────────────────────────────────────────────────────────────────
const Svg = ({ d, size = 17, color = P.accent, sw = 1.7, fill = 'none' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill}
    stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

function Toggle({ checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)} style={{
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
      background: checked ? P.accent : 'rgba(255,255,255,0.1)', position: 'relative', transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 2, left: checked ? 22 : 2,
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,.35)',
      }} />
    </div>
  );
}

function ScreenHeader({ title, onBack }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'16px 20px 14px', flexShrink:0, borderBottom:`1px solid ${P.border}` }}>
      <button onClick={onBack} style={{ width:32, height:32, borderRadius:8, background:'rgba(255,255,255,0.06)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:P.muted, flexShrink:0, transition:'background .15s' }}
        onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}
        onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.06)'}>
        <Svg d="M15 18l-6-6 6-6" size={16} color={P.muted} />
      </button>
      <span style={{ fontSize:15, fontWeight:600, color:P.text, letterSpacing:'-.01em' }}>{title}</span>
    </div>
  );
}

function SectionLabel({ children, sub }) {
  return (
    <div style={{ paddingTop:18, paddingBottom: sub ? 3 : 7 }}>
      <div style={{ fontSize:10.5, fontWeight:600, color:P.dim, letterSpacing:'.07em', textTransform:'uppercase' }}>{children}</div>
      {sub && <div style={{ fontSize:11, color:'rgba(255,255,255,0.28)', marginTop:3 }}>{sub}</div>}
    </div>
  );
}

function SettingRow({ label, sub, children }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 0', borderBottom:`1px solid ${P.border}`, gap:12 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, color:P.text }}>{label}</div>
        {sub && <div style={{ fontSize:11, color:P.dim, marginTop:2, lineHeight:1.4 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

function Pill({ options, value, onChange }) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:6, paddingTop:6 }}>
      {options.map(opt => (
        <button key={opt} onClick={() => onChange(opt)} style={{
          padding:'6px 13px', borderRadius:20, border:'none', cursor:'pointer',
          fontSize:12, fontWeight:500, transition:'all .15s',
          background: value === opt ? P.accent : 'rgba(255,255,255,0.06)',
          color: value === opt ? '#fff' : P.muted,
        }}>{opt}</button>
      ))}
    </div>
  );
}

function Pill3({ label, value, onChange }) {
  const opts = ['Baixo', 'Natural', 'Máximo'];
  return (
    <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,0.05)', borderRadius:10, padding:3 }}>
      {opts.map(o => (
        <button key={o} onClick={()=>onChange(o)} style={{
          flex:1, padding:'5px 0', borderRadius:8, border:'none', cursor:'pointer',
          fontSize:11, fontWeight:500, transition:'all .15s',
          background: value===o ? P.accent : 'none',
          color: value===o ? '#fff' : P.muted,
        }}>{o}</button>
      ))}
    </div>
  );
}

const inputStyle = {
  width:'100%', padding:'10px 12px', boxSizing:'border-box',
  background:'rgba(255,255,255,0.05)', border:`1px solid ${P.border}`,
  borderRadius:10, color:P.text, fontSize:13, outline:'none', fontFamily:'inherit', transition:'border-color .15s',
};
const btnPrimary = { padding:'12px', borderRadius:12, border:'none', cursor:'pointer', background:P.accent, color:'#fff', fontSize:13, fontWeight:600, transition:'opacity .15s' };
const btnGhost   = { padding:'12px 16px', borderRadius:12, border:`1px solid ${P.border}`, cursor:'pointer', background:'none', color:P.muted, fontSize:13 };

function authHeader() {
  const t = localStorage.getItem('athena_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}
async function apiGet(path) {
  try {
    const url = /^https?:\/\//i.test(path) ? path : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
    const r = await fetch(url,{headers:authHeader()});
    return r.ok ? r.json() : null;
  } catch { return null; }
}
async function apiPut(path, body) {
  try {
    const url = /^https?:\/\//i.test(path) ? path : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
    const r = await fetch(url,{method:'PUT',headers:{'Content-Type':'application/json',...authHeader()},body:JSON.stringify(body)});
    return r.ok ? r.json() : null;
  } catch { return null; }
}

// ── Inner screens ──────────────────────────────────────────────────────────────

function PerfilScreen({ onBack, showToast, userName }) {
  const _pf = (() => { try { return JSON.parse(localStorage.getItem('athena_perfil')||'{}'); } catch { return {}; } })();
  const [nome,      setNome]      = useState(_pf.nome      || userName || 'Vitor Leão');
  const [apelido,   setApelido]   = useState(_pf.apelido   || 'Vitor');
  const [profissao, setProfissao] = useState(_pf.profissao || 'Empreendedor');
  const [objetivo,  setObjetivo]  = useState(_pf.objetivo  || 'Construir a Athena e lançar uma versão beta.');
  const [rotina,    setRotina]    = useState(_pf.rotina    || 'noite');
  const [tags,      setTags]      = useState(_pf.tags      || ['tecnologia','negócios','produtividade']);
  const ALL_TAGS = ['tecnologia','negócios','estudos','design','programação','fé','saúde','produtividade','criação de conteúdo'];
  const toggleTag = t => setTags(prev => prev.includes(t) ? prev.filter(x=>x!==t) : [...prev,t]);
  const save = async () => {
    localStorage.setItem('athena_perfil', JSON.stringify({nome,apelido,profissao,objetivo,rotina,tags}));
    await apiPut(`${API_BASE}/auth/me`, {name:nome});
    showToast('Perfil atualizado. Athena vai considerar isso nas próximas respostas.');
  };
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Perfil" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'0 20px 32px'}}>
        <SectionLabel>Identidade</SectionLabel>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {[['Nome exibido',nome,setNome],['Como Athena deve te chamar',apelido,setApelido],['Profissão / ocupação',profissao,setProfissao]].map(([label,val,set])=>(
            <div key={label}>
              <div style={{fontSize:11,color:P.dim,marginBottom:5}}>{label}</div>
              <input value={val} onChange={e=>set(e.target.value)} style={inputStyle}
                onFocus={e=>e.target.style.borderColor=P.accent} onBlur={e=>e.target.style.borderColor=P.border}/>
            </div>
          ))}
        </div>
        <SectionLabel>Áreas de interesse</SectionLabel>
        <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
          {ALL_TAGS.map(t=>(
            <button key={t} onClick={()=>toggleTag(t)} style={{padding:'6px 13px',borderRadius:20,border:'none',cursor:'pointer',fontSize:12,fontWeight:500,transition:'all .15s',background:tags.includes(t)?P.accent:'rgba(255,255,255,0.06)',color:tags.includes(t)?'#fff':P.muted}}>{t}</button>
          ))}
        </div>
        <SectionLabel>Objetivo atual</SectionLabel>
        <textarea value={objetivo} onChange={e=>setObjetivo(e.target.value)} rows={3}
          style={{...inputStyle,resize:'vertical',minHeight:70,lineHeight:1.5}}
          onFocus={e=>e.target.style.borderColor=P.accent} onBlur={e=>e.target.style.borderColor=P.border}/>
        <SectionLabel>Período de maior produtividade</SectionLabel>
        <Pill options={['manhã','tarde','noite','madrugada','varia muito']} value={rotina} onChange={setRotina}/>
        <div style={{display:'flex',gap:10,marginTop:24}}>
          <button onClick={save} style={{...btnPrimary,flex:1}}>Salvar perfil</button>
          <button onClick={onBack} style={btnGhost}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function ContaScreen({ onBack, showToast }) {
  const [showDel, setShowDel] = useState(false);
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Conta" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'0 20px 32px'}}>
        <SectionLabel>Acesso</SectionLabel>
        <SettingRow label="Email da conta" sub="vitor@outframe.com">
          <button onClick={()=>showToast('Em breve.')} style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Alterar</button>
        </SettingRow>
        <SettingRow label="Senha" sub="Autenticação local">
          <button onClick={()=>showToast('Em breve.')} style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Alterar</button>
        </SettingRow>
        <SectionLabel>Plano atual</SectionLabel>
        <div style={{padding:'14px 16px',borderRadius:14,background:'rgba(59,130,246,0.07)',border:`1px solid ${P.borderHi}`,marginBottom:4}}>
          <div style={{fontSize:14,fontWeight:600,color:P.accent}}>Beta Local</div>
          <div style={{fontSize:11,color:P.muted,marginTop:3}}>Acesso antecipado ao ambiente local da Athena.</div>
        </div>
        <SectionLabel>Sessões</SectionLabel>
        {['Notebook Linux — Ativa agora','Android — há 2 dias','Chrome Web — há 5 dias'].map((s,i)=>(
          <SettingRow key={i} label={s.split('—')[0].trim()} sub={s.split('—')[1]?.trim()}>
            <div style={{width:8,height:8,borderRadius:'50%',background:i===0?'#22c55e':P.dim,flexShrink:0}}/>
          </SettingRow>
        ))}
        <button onClick={()=>showToast('Em breve.')} style={{width:'100%',marginTop:10,padding:'10px',borderRadius:10,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Encerrar outras sessões</button>
        <SectionLabel>Zona de perigo</SectionLabel>
        {!showDel ? (
          <button onClick={()=>setShowDel(true)} style={{width:'100%',padding:'11px',borderRadius:10,border:`1px solid ${P.redBorder}`,background:P.redCard,color:P.red,fontSize:13,cursor:'pointer',fontWeight:500}}>Excluir conta</button>
        ) : (
          <div style={{padding:16,borderRadius:14,border:`1px solid ${P.redBorder}`,background:P.redCard}}>
            <div style={{fontSize:13,color:P.red,fontWeight:600,marginBottom:6}}>Tem certeza?</div>
            <div style={{fontSize:12,color:P.muted,marginBottom:14,lineHeight:1.5}}>Esta ação é permanente. Todos os dados serão apagados.</div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setShowDel(false)} style={{flex:1,padding:'9px',borderRadius:8,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Cancelar</button>
              <button onClick={()=>showToast('Em breve.')} style={{flex:1,padding:'9px',borderRadius:8,border:'none',background:P.red,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>Excluir conta</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MemoriaScreen({ onBack, showToast, token }) {
  const [active,    setActive]    = useState(true);
  const [memories,  setMemories]  = useState([]);
  const [search,    setSearch]    = useState('');
  const [showClear, setShowClear] = useState(false);
  const [loading,   setLoading]   = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/memory/list`,{headers:authHeader()}).then(r=>r.json())
      .then(d=>setMemories(Array.isArray(d.memories)?d.memories:[]))
      .catch(()=>setMemories([])).finally(()=>setLoading(false));
  },[token]);
  const clearAll = async () => {
    await fetch(`${API_BASE}/memory/clear`,{method:'DELETE',headers:authHeader()});
    setMemories([]); setShowClear(false); showToast('Todas as memórias foram apagadas.');
  };
  const filtered = memories.filter(m=>!search||(m.content||m.text||'').toLowerCase().includes(search.toLowerCase()));
  const catColors = {Projeto:'#3b82f6',Preferência:'#8b5cf6',Rotina:'#10b981',Pessoa:'#f59e0b',Trabalho:'#06b6d4',Ideia:'#ec4899',Importante:'#ef4444'};
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Memória" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'0 20px 32px'}}>
        <SettingRow label="Memória ativa" sub="Athena usa o que lembra sobre você"><Toggle checked={active} onChange={setActive}/></SettingRow>
        {!active && <div style={{padding:'10px 12px',borderRadius:10,background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.15)',marginTop:6,fontSize:12,color:'#f59e0b',lineHeight:1.5}}>Suas memórias não serão apagadas, apenas ignoradas enquanto desligado.</div>}
        <SectionLabel>Memórias salvas {memories.length>0?`(${memories.length})`:''}</SectionLabel>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar nas memórias…" style={{...inputStyle,fontSize:12,marginBottom:10}}/>
        {loading ? <div style={{textAlign:'center',color:P.dim,fontSize:12,padding:'28px 0'}}>Carregando…</div>
         : filtered.length===0 ? <div style={{textAlign:'center',color:P.dim,fontSize:12,padding:'28px 0'}}>{memories.length===0?'Nenhuma memória salva ainda.':'Nenhum resultado.'}</div>
         : <div style={{display:'flex',flexDirection:'column',gap:7}}>
            {filtered.slice(0,40).map((m,i)=>(
              <div key={m.id||i} style={{padding:'11px 14px',borderRadius:12,background:P.card,border:`1px solid ${P.border}`}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div style={{fontSize:12,color:P.text,flex:1,lineHeight:1.55,marginRight:8}}>{(m.content||m.text||'').slice(0,130)}{(m.content||m.text||'').length>130?'…':''}</div>
                  <button onClick={()=>showToast('Em breve.')} style={{width:20,height:20,flexShrink:0,background:'none',border:'none',cursor:'pointer',color:P.dim,fontSize:15}}>×</button>
                </div>
                {m.category&&<span style={{display:'inline-block',marginTop:6,fontSize:10,color:catColors[m.category]||P.dim,background:`${catColors[m.category]||P.dim}18`,padding:'2px 8px',borderRadius:20}}>{m.category}</span>}
              </div>
            ))}
           </div>}
        <div style={{display:'flex',gap:8,marginTop:20}}>
          <button onClick={()=>showToast('Em breve.')} style={{flex:1,padding:'10px',borderRadius:10,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Exportar</button>
          <button onClick={()=>showToast('Em breve.')} style={{flex:1,padding:'10px',borderRadius:10,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Importar</button>
          <button onClick={()=>setShowClear(true)} style={{flex:1,padding:'10px',borderRadius:10,border:`1px solid ${P.redBorder}`,background:P.redCard,color:P.red,fontSize:12,cursor:'pointer'}}>Limpar</button>
        </div>
        {showClear&&<div style={{marginTop:12,padding:14,borderRadius:12,border:`1px solid ${P.redBorder}`,background:P.redCard}}>
          <div style={{fontSize:12,color:P.red,fontWeight:600,marginBottom:6}}>Limpar todas as memórias?</div>
          <div style={{fontSize:11,color:P.muted,marginBottom:12}}>Esta ação não pode ser desfeita.</div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>setShowClear(false)} style={{flex:1,padding:'8px',borderRadius:8,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer'}}>Cancelar</button>
            <button onClick={clearAll} style={{flex:1,padding:'8px',borderRadius:8,border:'none',background:P.red,color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer'}}>Limpar tudo</button>
          </div>
        </div>}
      </div>
    </div>
  );
}

// ── Interação Social — sub-section for InteligenciaScreen ─────────────────────
function InteracaoSocial({ state, onChange }) {
  const { expressividade, emojisLevel, gifsLevel, continuousLevel, emotionalLevel, adaptative } = state;
  const setField = (k, v) => onChange({ ...state, [k]: v });

  const setExpressividade = (val) => {
    const presets = {
      'Profissional': { emojisLevel:'Baixo',   gifsLevel:'Baixo',   continuousLevel:'Baixo',   emotionalLevel:'Baixo'   },
      'Equilibrado':  { emojisLevel:'Natural',  gifsLevel:'Baixo',   continuousLevel:'Natural',  emotionalLevel:'Natural'  },
      'Expressivo':   { emojisLevel:'Máximo',   gifsLevel:'Natural', continuousLevel:'Máximo',   emotionalLevel:'Máximo'   },
      'Social':       { emojisLevel:'Máximo',   gifsLevel:'Máximo',  continuousLevel:'Máximo',   emotionalLevel:'Máximo'   },
    };
    onChange({ ...state, expressividade: val, ...(presets[val] || {}) });
  };

  return (
    <>
      <SectionLabel sub="Controle como Athena se expressa em conversas mais humanas.">Interação Social</SectionLabel>

      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,color:P.dim,marginBottom:5}}>Nível de expressividade</div>
        <Pill options={['Profissional','Equilibrado','Expressivo','Social']} value={expressividade} onChange={setExpressividade}/>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:12,marginTop:4}}>
        {[
          ['Emojis',                emojisLevel,     v=>setField('emojisLevel',v),     'Athena pode usar emojis para expressar tom e intenção.'],
          ['GIFs e stickers',       gifsLevel,       v=>setField('gifsLevel',v),       'Em breve — configuração preparada para uso futuro.'],
          ['Respostas contínuas',   continuousLevel, v=>setField('continuousLevel',v), 'Athena mantém presença ativa e continua a conversa naturalmente.'],
          ['Reações emocionais',    emotionalLevel,  v=>setField('emotionalLevel',v),  'Athena demonstra acolhimento, entusiasmo ou cuidado com discrição.'],
        ].map(([label, val, set, sub]) => (
          <div key={label} style={{padding:'12px 14px',borderRadius:13,background:P.card,border:`1px solid ${P.border}`}}>
            <div style={{fontSize:12,fontWeight:500,color:P.text,marginBottom:2}}>{label}</div>
            <div style={{fontSize:11,color:P.dim,marginBottom:8}}>{sub}</div>
            <Pill3 value={val} onChange={set}/>
          </div>
        ))}
      </div>

      <SettingRow label="Personalidade adaptativa" sub="Athena adapta tom e expressividade ao contexto da conversa">
        <Toggle checked={adaptative} onChange={v=>setField('adaptative',v)}/>
      </SettingRow>
    </>
  );
}

function InteligenciaScreen({ onBack, showToast, initPrefs }) {
  const _ai = (() => { try { return JSON.parse(localStorage.getItem('athena_ai_prefs')||'{}'); } catch { return {}; } })();
  const _soc = (() => { try { return JSON.parse(localStorage.getItem('athena_social_prefs')||'{}'); } catch { return {}; } })();
  const src  = Object.keys(_ai).length > 0 ? _ai : (initPrefs || {});

  const [estilo,       setEstilo]       = useState(src.estilo       || 'Equilibrada');
  const [tom,          setTom]          = useState(src.tom          || 'Estratégico');
  const [iniciativa,   setIniciativa]   = useState(src.iniciativa   || 'Médio');
  const [profundidade, setProfundidade] = useState(src.profundidade || 'Normal');
  const [organizacao,  setOrganizacao]  = useState(src.organizacao  || 'Texto corrido');
  const [criatividade, setCriatividade] = useState(src.criatividade ?? 50);
  const [sincero,      setSincero]      = useState(src.sincero      ?? true);
  const [continuo,     setContinuo]     = useState(src.continuo     ?? true);
  const [instrucoes,   setInstrucoes]   = useState(src.instrucoes   || '');

  const [social, setSocial] = useState({
    expressividade:   _soc.expressividade   || 'Equilibrado',
    emojisLevel:      _soc.emojisLevel      || 'Natural',
    gifsLevel:        _soc.gifsLevel        || 'Baixo',
    continuousLevel:  _soc.continuousLevel  || 'Natural',
    emotionalLevel:   _soc.emotionalLevel   || 'Natural',
    adaptative:       _soc.adaptative       ?? true,
  });

  useEffect(() => {
    if (!initPrefs || Object.keys(initPrefs).length===0) return;
    if (localStorage.getItem('athena_ai_prefs')) return;
    localStorage.setItem('athena_ai_prefs', JSON.stringify(initPrefs));
  }, [initPrefs]);

  const save = async () => {
    const aiPrefs = { estilo, tom, iniciativa, profundidade, organizacao, criatividade, sincero, continuo, instrucoes };
    localStorage.setItem('athena_ai_prefs',    JSON.stringify(aiPrefs));
    localStorage.setItem('athena_social_prefs', JSON.stringify(social));
    const existing = await apiGet(`${API_BASE}/auth/preferences`) || {};
    await apiPut(`${API_BASE}/auth/preferences`, { ...existing, ...aiPrefs, social });
    showToast('Inteligência personalizada salva. Athena usará nas próximas respostas.');
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Inteligência personalizada" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'0 20px 32px'}}>
        <SectionLabel>Estilo de resposta</SectionLabel>
        <Pill options={['Objetiva','Equilibrada','Detalhada','Técnica','Criativa']} value={estilo} onChange={setEstilo}/>
        <SectionLabel>Tom de comunicação</SectionLabel>
        <Pill options={['Natural','Profissional','Amigável','Direto','Estratégico']} value={tom} onChange={setTom}/>
        <SectionLabel>Nível de iniciativa</SectionLabel>
        <Pill options={['Baixo','Médio','Alto']} value={iniciativa} onChange={setIniciativa}/>
        <SectionLabel>Profundidade</SectionLabel>
        <Pill options={['Rápida','Normal','Profunda']} value={profundidade} onChange={setProfundidade}/>
        <SectionLabel>Organização das respostas</SectionLabel>
        <Pill options={['Texto corrido','Passo a passo','Checklist','Com exemplos']} value={organizacao} onChange={setOrganizacao}/>
        <SectionLabel>Criatividade</SectionLabel>
        <input type="range" min={0} max={100} value={criatividade} onChange={e=>setCriatividade(+e.target.value)} style={{width:'100%',accentColor:P.accent,marginTop:8}}/>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:P.dim,marginTop:3}}>
          <span>Conservadora</span><span>Equilibrada</span><span>Criativa</span>
        </div>
        <div style={{marginTop:6}}>
          <SettingRow label="Sinceridade direta" sub="Athena alerta com mais firmeza quando necessário"><Toggle checked={sincero} onChange={setSincero}/></SettingRow>
          <SettingRow label="Usar contexto de conversas anteriores" sub="Retomadas contextuais entre sessões"><Toggle checked={continuo} onChange={setContinuo}/></SettingRow>
        </div>
        <SectionLabel>Instruções personalizadas</SectionLabel>
        <textarea value={instrucoes} onChange={e=>setInstrucoes(e.target.value)} rows={4}
          placeholder="Gosto de respostas diretas, mas com explicação suficiente para eu aplicar."
          style={{...inputStyle,resize:'vertical',minHeight:88,lineHeight:1.5}}
          onFocus={e=>e.target.style.borderColor=P.accent} onBlur={e=>e.target.style.borderColor=P.border}/>

        {/* ── Interação Social ───────────────────────────────────────────── */}
        <div style={{marginTop:8,paddingTop:4,borderTop:`1px solid ${P.border}`}}>
          <InteracaoSocial state={social} onChange={setSocial}/>
        </div>

        <div style={{display:'flex',gap:10,marginTop:24}}>
          <button onClick={save} style={{...btnPrimary,flex:1}}>Salvar</button>
          <button onClick={()=>showToast('Restaurado ao padrão.')} style={btnGhost}>Restaurar padrão</button>
        </div>
      </div>
    </div>
  );
}

function AplicativosScreen({ onBack, showToast }) {
  const [conn, setConn] = useState({});
  const APPS = [
    {id:'drive',name:'Google Drive',desc:'Ler arquivos autorizados',icon:'📁',soon:false},
    {id:'gmail',name:'Gmail',desc:'Resumir e organizar emails',icon:'✉️',soon:false},
    {id:'calendar',name:'Google Calendar',desc:'Consultar agenda e compromissos',icon:'📅',soon:false},
    {id:'github',name:'GitHub',desc:'Repositórios e issues',icon:'⚙️',soon:false},
    {id:'notion',name:'Notion',desc:'Páginas, bancos de dados e notas',icon:'📓',soon:false},
    {id:'whatsapp',name:'WhatsApp',desc:'Contexto e automações',icon:'💬',soon:true},
    {id:'instagram',name:'Instagram',desc:'Criação e análise de conteúdo',icon:'📸',soon:true},
    {id:'discord',name:'Discord',desc:'Bots e comunidades',icon:'🎮',soon:true},
  ];
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Aplicativos conectados" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'10px 20px 32px'}}>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {APPS.map(app=>(
            <div key={app.id} style={{display:'flex',alignItems:'center',gap:12,padding:'14px',borderRadius:14,background:P.card,border:`1px solid ${P.border}`}}>
              <div style={{fontSize:22,flexShrink:0,width:38,textAlign:'center'}}>{app.icon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,color:P.text}}>{app.name}</div>
                <div style={{fontSize:11,color:P.muted,marginTop:1}}>{app.desc}</div>
              </div>
              {app.soon ? (
                <span style={{fontSize:10,color:P.dim,background:'rgba(255,255,255,0.05)',padding:'4px 9px',borderRadius:20,flexShrink:0}}>Em breve</span>
              ) : (
                <button onClick={()=>{if(conn[app.id]){setConn(c=>({...c,[app.id]:false}));showToast(`${app.name} desconectado.`);}else showToast('Em breve.');}} style={{padding:'6px 14px',borderRadius:8,border:'none',cursor:'pointer',fontSize:12,fontWeight:500,flexShrink:0,background:conn[app.id]?'rgba(239,68,68,0.12)':'rgba(59,130,246,0.14)',color:conn[app.id]?P.red:P.accent}}>
                  {conn[app.id]?'Desconectar':'Conectar'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Notification helper (exported for use in other components) ─────────────────
export function sendAthenaNotification({ title='Athena', body='', tag='athena', icon='/athena-favicon.png', data={} }={}) {
  // The platform-aware provider owns permission and native delivery. This
  // legacy export remains for callers that expect a local web notification.
  void icon;
  return sendWebAthenaNotification({ title, body, tag, data });
}

function PermissoesScreen({ onBack, showToast }) {
  const deviceContext = useAthenaDeviceContext();
  const remoteConfig = useAthenaRemoteConfig();
  const deviceContextReleased = remoteConfig.featureEnabled('deviceContext');
  const deviceContextAvailable = deviceContext.available === true && deviceContextReleased;
  const devicePreferences = deviceContext.preferences;
  const updateDeviceContextPreferences = async (patch) => {
    if (!deviceContextAvailable) {
      showToast(deviceContext.available
        ? 'O contexto do dispositivo ainda não foi liberado para esta versão.'
        : 'O contexto do dispositivo requer o aplicativo Android compatível.');
      return;
    }
    const result = await deviceContext.setPreferences(patch);
    if (!result?.ok) showToast('Não foi possível salvar essa preferência de contexto.');
  };
  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState(()=>loadNotifPrefs());
  const saveNPrefs = (patch) => {
    setNotifPrefs(prev => {
      const next = { ...prev, ...patch };
      saveNotifPrefs(next);
      // A rotina no servidor precisa respeitar a mesma escolha feita no app;
      // salvar somente no localStorage fazia o toggle não valer no celular
      // quando a Athena estava fechada.
      (async () => {
        const existing = await apiGet(`${API_BASE}/auth/preferences`) || {};
        await apiPut(`${API_BASE}/auth/preferences`, { ...existing, notifications: next });
      })();
      return next;
    });
  };

  // idle | granted | denied | unsupported | loading
  const [notifSt, setNotifSt] = useState('idle');
  const [camSt,   setCamSt]   = useState('idle');
  const [locSt,   setLocSt]   = useState('idle');
  const [scrSt,   setScrSt]   = useState('idle');
  const [floatingOverlayStatus, setFloatingOverlayStatus] = useState(() => ({
    available: false,
    configured: false,
    enabled: false,
    permission: 'unavailable',
    permissionGranted: false,
    interactionLevel: 'medium',
  }));
  const [floatingOverlayBusy, setFloatingOverlayBusy] = useState(false);

  const [camActive, setCamActive] = useState(false);
  const [camFacing, setCamFacing] = useState('user');
  const camVideoRef  = useRef(null);
  const camStreamRef = useRef(null);

  const [scrActive, setScrActive] = useState(false);
  const scrVideoRef  = useRef(null);
  const scrStreamRef = useRef(null);

  const [locData, setLocData] = useState(null);

  // One provider serves both PWA and Capacitor. The Android path never reads
  // `window.Notification`, which is not Android's POST_NOTIFICATIONS grant.
  useEffect(() => {
    let active = true;
    getAthenaNotificationStatus().then((status) => {
      if (!active) return;
      const permission = status?.permission;
      setNotifSt(permission === 'granted' || permission === 'denied'
        ? permission
        : (permission === 'unsupported' || permission === 'unavailable' ? 'unsupported' : 'idle'));
    }).catch(() => {
      if (active) setNotifSt('unsupported');
    });

    // Status probing is intentionally passive. The special Android overlay
    // access page can only be opened by `toggleFloatingOverlay` below.
    getAthenaFloatingOverlayStatus().then((status) => {
      if (active) setFloatingOverlayStatus(status);
    }).catch(() => {
      if (active) setFloatingOverlayStatus((current) => ({ ...current, available: false }));
    });

    if (navigator.permissions) {
      navigator.permissions.query({name:'camera'}).then(r=>{
        if (r.state !== 'prompt') setCamSt(r.state === 'granted' ? 'granted' : 'denied');
      }).catch(()=>{});
      navigator.permissions.query({name:'geolocation'}).then(r=>{
        if (r.state !== 'prompt') setLocSt(r.state === 'granted' ? 'granted' : 'denied');
      }).catch(()=>{});
    }

    return () => {
      active = false;
      camStreamRef.current?.getTracks().forEach(t=>t.stop());
      camStreamRef.current = null;
      scrStreamRef.current?.getTracks().forEach(t=>t.stop());
      scrStreamRef.current = null;
    };
  }, []);

  // ── Notifications ─────────────────────────────────────────────────────────────
  const requestNotif = async () => {
    const permission = await requestAthenaNotificationsPermission();
    const status = permission === 'granted' || permission === 'denied'
      ? permission
      : (permission === 'unsupported' || permission === 'unavailable' ? 'unsupported' : 'idle');
    setNotifSt(status);
    if (permission === 'granted') {
      try {
        const result = await registerAthenaNotifications(localStorage.getItem('athena_token'), {
          // This button is an explicit user activation. On Android it is the
          // one place allowed to ask the native plugin for an FCM token.
          userInitiated: IS_NATIVE_APP,
        });
        if (IS_NATIVE_APP && !result?.ok) throw new Error(result?.reason || 'native_push_unavailable');
        showToast('Notificações ativadas!');
      } catch {
        showToast(IS_NATIVE_APP
          ? 'A permissão foi concedida, mas o push nativo ainda não pôde ser ativado neste aparelho.'
          : 'A permissão foi concedida, mas o push não pôde ser ativado.');
      }
    }
    else if (permission === 'denied') showToast(IS_NATIVE_APP ? 'Permissão de notificações negada pelo Android.' : 'Permissão de notificações negada pelo navegador.');
    else showToast('Notificações nativas ainda não estão disponíveis neste aparelho.');
  };
  const testNotif = async () => {
    if (!IS_NATIVE_APP) {
      const sent = sendAthenaNotification({ body: 'Notificações da Athena estão funcionando.' });
      if (!sent) showToast('Não consegui enviar a notificação de teste.');
      return;
    }
    try {
      await testAthenaNotifications(localStorage.getItem('athena_token'));
      showToast('Teste de notificação enviado para este aparelho.');
    } catch {
      showToast('Ainda não foi possível testar o push nativo neste aparelho.');
    }
  };

  // ── Floating Quick Athena ──────────────────────────────────────────────────
  const toggleFloatingOverlay = async (enabled) => {
    if (floatingOverlayBusy) return;
    setFloatingOverlayBusy(true);
    try {
      if (!enabled) {
        const status = await disableAthenaFloatingOverlay();
        setFloatingOverlayStatus(status);
        if (status.available) showToast('Athena flutuante desativada.');
        else showToast('A Athena flutuante requer o aplicativo Android atualizado.');
        return;
      }

      // This is a direct settings action. Nothing in NativeAppShell, login,
      // notifications or a chat response can call this permission path.
      const permission = await requestAthenaFloatingOverlayPermission();
      setFloatingOverlayStatus(permission);
      if (!permission.available) {
        showToast('A Athena flutuante requer o aplicativo Android atualizado.');
        return;
      }
      if (!permission.permissionGranted) {
        showToast('Permita “exibir sobre outros apps” para ativar a Athena flutuante.');
        return;
      }

      const status = await enableAthenaFloatingOverlay({ visualState: 'idle' });
      setFloatingOverlayStatus(status);
      if (status.enabled || status.running) {
        showToast('Athena flutuante ativada. Ela aparece quando você sair do app.');
      } else {
        showToast('Não foi possível iniciar a Athena flutuante neste aparelho.');
      }
    } catch {
      showToast('Não foi possível alterar a Athena flutuante agora.');
    } finally {
      setFloatingOverlayBusy(false);
    }
  };

  const setFloatingInteractionLevel = async (level) => {
    if (floatingOverlayBusy || !floatingOverlayStatus.available) return;
    setFloatingOverlayBusy(true);
    try {
      const status = await setAthenaFloatingOverlayInteractionLevel(level);
      setFloatingOverlayStatus(status);
      const label = level === 'low' ? 'Baixo' : level === 'high' ? 'Alto' : 'Médio';
      showToast(`Interação da Athena flutuante: ${label}.`);
    } catch {
      showToast('Não foi possível atualizar o nível de interação agora.');
    } finally {
      setFloatingOverlayBusy(false);
    }
  };

  // ── Camera ───────────────────────────────────────────────────────────────────
  const requestCam = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast('Câmera não disponível neste dispositivo.');
      setCamSt('unsupported'); return;
    }
    setCamSt('loading');
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      s.getTracks().forEach(t=>t.stop());
      setCamSt('granted');
      showToast('Câmera autorizada.');
    } catch {
      setCamSt('denied');
      showToast('Permissão de câmera negada.');
    }
  };

  const startCamWith = async (facing) => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } });
      camStreamRef.current = s;
      if (camVideoRef.current) camVideoRef.current.srcObject = s;
      setCamActive(true);
    } catch { showToast('Não foi possível acessar a câmera.'); }
  };
  const startCam = () => startCamWith(camFacing);
  const stopCam  = () => {
    camStreamRef.current?.getTracks().forEach(t=>t.stop());
    camStreamRef.current = null;
    setCamActive(false);
  };
  const flipCam = () => {
    const next = camFacing === 'user' ? 'environment' : 'user';
    setCamFacing(next);
    if (camActive) { stopCam(); setTimeout(() => startCamWith(next), 120); }
  };

  // ── Location ─────────────────────────────────────────────────────────────────
  const requestLoc = () => {
    if (!navigator.geolocation) {
      showToast('Localização não disponível neste dispositivo.');
      setLocSt('unsupported'); return;
    }
    setLocSt('loading');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocSt('granted');
        setLocData({
          lat: pos.coords.latitude.toFixed(5),
          lng: pos.coords.longitude.toFixed(5),
          acc: Math.round(pos.coords.accuracy),
          ts: new Date(pos.timestamp).toLocaleTimeString('pt-BR'),
        });
        showToast('Localização atualizada.');
      },
      () => { setLocSt('denied'); showToast('Permissão de localização negada.'); },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  // ── Screen share ──────────────────────────────────────────────────────────────
  const requestScr = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      showToast('Compartilhamento de tela não disponível neste dispositivo.');
      setScrSt('unsupported'); return;
    }
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      scrStreamRef.current = s;
      if (scrVideoRef.current) scrVideoRef.current.srcObject = s;
      setScrActive(true);
      setScrSt('granted');
      s.getVideoTracks()[0]?.addEventListener('ended', stopScr);
    } catch { showToast('Compartilhamento de tela cancelado.'); }
  };
  const stopScr = () => {
    scrStreamRef.current?.getTracks().forEach(t=>t.stop());
    scrStreamRef.current = null;
    setScrActive(false);
  };

  // ── UI helpers ────────────────────────────────────────────────────────────────
  const Badge = ({ st }) => {
    const m = {
      idle:        ['Não solicitado', P.dim,         'rgba(255,255,255,0.06)'],
      granted:     ['Permitido',      '#22c55e',     'rgba(34,197,94,0.12)'],
      denied:      ['Negado',         P.red,         P.redCard],
      unsupported: ['Indisponível',   P.dim,         'rgba(255,255,255,0.03)'],
      loading:     ['Verificando…',   P.accentMuted, 'rgba(59,130,246,0.1)'],
    };
    const [label, color, bg] = m[st] || m.idle;
    return (
      <span style={{ fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:20, background:bg, color, flexShrink:0 }}>
        {label}
      </span>
    );
  };

  const Box = ({ children }) => (
    <div style={{ background:P.card, borderRadius:14, border:`1px solid ${P.border}`, padding:'4px 16px 14px', marginBottom:4 }}>
      {children}
    </div>
  );

  const BtnPrim = ({ onClick, children }) => (
    <button onClick={onClick} style={{
      padding:'9px 14px', borderRadius:10, border:'none', cursor:'pointer',
      background:P.accent, color:'#fff', fontSize:12, fontWeight:600,
      width:'100%', transition:'opacity .15s',
    }}
      onMouseEnter={e=>e.currentTarget.style.opacity='.85'}
      onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
      {children}
    </button>
  );

  const BtnGhost = ({ onClick, disabled, children }) => (
    <button onClick={disabled ? undefined : onClick} style={{
      padding:'9px 14px', borderRadius:10,
      border:`1px solid ${disabled ? 'rgba(255,255,255,0.05)' : P.border}`,
      cursor:disabled ? 'default' : 'pointer',
      background:'rgba(255,255,255,0.03)',
      color:disabled ? P.dim : P.muted, fontSize:12, fontWeight:500,
      width:'100%', opacity:disabled ? .5 : 1, transition:'all .15s',
    }}>
      {children}
    </button>
  );

  const VideoPreview = ({ vRef, onStop, onFlip, maxH }) => (
    <div style={{ borderRadius:12, overflow:'hidden', background:'#000', position:'relative', marginTop:4 }}>
      <video ref={vRef} autoPlay muted playsInline
        style={{ width:'100%', objectFit:'cover', display:'block', maxHeight: maxH || 220 }} />
      <div style={{ position:'absolute', top:8, right:8, display:'flex', gap:6 }}>
        {onFlip && (
          <button onClick={onFlip} style={{ padding:'5px 10px', borderRadius:8,
            background:'rgba(0,0,0,0.55)', color:'#fff', border:'none', cursor:'pointer', fontSize:11 }}>
            Virar câmera
          </button>
        )}
        <button onClick={onStop} style={{ padding:'5px 10px', borderRadius:8,
          background:'rgba(220,38,38,0.75)', color:'#fff', border:'none', cursor:'pointer', fontSize:11 }}>
          Encerrar
        </button>
      </div>
    </div>
  );

  const Hint = ({ children }) => (
    <div style={{ fontSize:11, color:P.dim, lineHeight:1.55, marginTop:6 }}>{children}</div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      <ScreenHeader title="Permissões" onBack={onBack} />
      <div style={{ flex:1, overflowY:'auto', padding:'0 20px 40px' }}>

        {/* ── NOTIFICAÇÕES ── */}
        <SectionLabel>Notificações</SectionLabel>
        <Box>
          <SettingRow label="Notificações do sistema" sub="Alertas, lembretes e mensagens da Athena">
            <Badge st={notifSt} />
          </SettingRow>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8 }}>
            {notifSt !== 'granted' && notifSt !== 'unsupported' && (
              <BtnPrim onClick={requestNotif}>Solicitar permissão de notificação</BtnPrim>
            )}
            {notifSt === 'granted' && (
              <BtnGhost onClick={testNotif}>Enviar notificação de teste</BtnGhost>
            )}
            {notifSt === 'denied' && (
              <Hint>{IS_NATIVE_APP ? 'Para reativar, acesse as configurações do Android e permita notificações para a Athena.' : 'Para reativar, acesse as configurações do navegador e permita notificações para este site.'}</Hint>
            )}
          </div>

          {/* Notification preferences */}
          <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${P.border}` }}>
            <SettingRow label="Notificações inteligentes" sub="Bom dia, boa tarde e boa noite contextuais">
              <Toggle checked={notifPrefs.enabled} onChange={v=>saveNPrefs({enabled:v})}/>
            </SettingRow>
            {notifPrefs.enabled && (<>
              <div style={{ fontSize:11, color:P.dim, margin:'8px 0 4px' }}>Horários ativos</div>
              <div style={{ display:'flex', gap:8 }}>
                {[['morning','☀️ Manhã'],['afternoon','🌤️ Tarde'],['evening','🌙 Noite']].map(([k,l])=>(
                  <button key={k} onClick={()=>saveNPrefs({[k]:!notifPrefs[k]})} style={{
                    flex:1, padding:'6px 0', borderRadius:9, fontSize:11, fontWeight:500,
                    border:`1px solid ${notifPrefs[k]?P.borderHi:P.border}`,
                    background:notifPrefs[k]?P.cardHi:'none',
                    color:notifPrefs[k]?P.accent:P.muted, cursor:'pointer', transition:'all .15s',
                  }}>{l}</button>
                ))}
              </div>
              <div style={{ fontSize:11, color:P.dim, margin:'10px 0 4px' }}>Frequência</div>
              <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,0.05)', borderRadius:10, padding:3 }}>
                {['Baixa','Normal','Alta'].map(f=>(
                  <button key={f} onClick={()=>saveNPrefs({frequency:f})} style={{
                    flex:1, padding:'5px 0', borderRadius:8, fontSize:11, fontWeight:500,
                    border:'none', cursor:'pointer', transition:'all .15s',
                    background:notifPrefs.frequency===f?P.accent:'none',
                    color:notifPrefs.frequency===f?'#fff':P.muted,
                  }}>{f}</button>
                ))}
              </div>
            </>)}
          </div>
        </Box>

        {/* ── ATHENA FLUTUANTE — OPT-IN ANDROID ───────────────────────────── */}
        <SectionLabel sub="Configurada no primeiro uso; aparece apenas quando você sair do app">Athena flutuante</SectionLabel>
        <Box>
          <SettingRow
            label="Quick Athena sobre outros apps"
            sub="Uma janela rápida sobre outros apps. A conversa é usada só durante o pedido; tokens e histórico não ficam na bolha."
          >
            {floatingOverlayStatus.available ? (
              <Toggle
                checked={floatingOverlayStatus.enabled === true && floatingOverlayStatus.configured === true && floatingOverlayStatus.userHidden !== true}
                // After the first guided setup this remains a true on/off
                // control. Re-enabling reuses the already-granted Android
                // access when possible; it never grants permission silently.
                onChange={(enabled) => { void toggleFloatingOverlay(enabled); }}
              />
            ) : (
              <span style={{ fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:20, background:'rgba(255,255,255,0.04)', color:P.dim }}>
                {floatingOverlayStatus.available ? 'Desativada' : 'Indisponível'}
              </span>
            )}
          </SettingRow>
          <Hint>
            {!floatingOverlayStatus.available
              ? 'Disponível apenas em uma versão Android da Athena que inclua o atalho flutuante.'
              : floatingOverlayBusy
                ? 'Atualizando acesso da Athena flutuante…'
                : floatingOverlayStatus.permission === 'denied'
                  ? 'O acesso “exibir sobre outros apps” está bloqueado. Ative-o no Android para usar este recurso.'
                  : floatingOverlayStatus.userHidden
                    ? 'Você removeu o atalho flutuante pelo X do Android. Ative o controle se quiser trazê-lo de volta.'
                  : floatingOverlayStatus.enabled && floatingOverlayStatus.configured
                    ? 'Ela fica oculta enquanto a Athena está aberta. Arraste para o X no rodapé para desligá-la.'
                    : 'A Quick Athena está desativada. Ative o controle para trazê-la de volta; o Android pedirá acesso somente se ele não estiver concedido.'}
          </Hint>
          {floatingOverlayStatus.available && (
            <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${P.border}` }}>
              <div style={{ fontSize:12, color:P.text, fontWeight:600 }}>Nível de interação</div>
              <Hint>Controla a intensidade visual e a reatividade da bolha. Não cria mensagens nem lembretes por conta própria.</Hint>
              <div style={{ display:'flex', gap:6, marginTop:9 }} role="group" aria-label="Nível de interação da Athena flutuante">
                {[
                  ['low', 'Baixo', 'Movimentos discretos'],
                  ['medium', 'Médio', 'Presença visual equilibrada'],
                  ['high', 'Alto', 'Animações e mudanças visuais mais evidentes'],
                ].map(([level, label, title]) => {
                  const selected = (floatingOverlayStatus.interactionLevel || 'medium') === level;
                  return <button key={level} type="button" title={title}
                    disabled={floatingOverlayBusy}
                    onClick={() => { void setFloatingInteractionLevel(level); }}
                    style={{ flex:1, minHeight:34, padding:'5px 4px', borderRadius:9, cursor:floatingOverlayBusy?'not-allowed':'pointer',
                      border:`1px solid ${selected ? P.borderHi : P.border}`,
                      background:selected ? P.cardHi : 'rgba(255,255,255,0.02)',
                      color:selected ? '#bfdbfe' : P.muted, fontSize:11, fontWeight:selected?700:500 }}>
                    {label}
                  </button>;
                })}
              </div>
            </div>
          )}
        </Box>

        {/* ── CONTEXTO DO DISPOSITIVO — FASE 5 ── */}
        <SectionLabel sub="Sempre desligado até sua escolha explícita">Contexto do dispositivo</SectionLabel>
        <Box>
          <SettingRow
            label="Usar contexto do dispositivo"
            sub="Sinais mínimos de bateria, rede, áudio e estado do app. Não inclui localização, microfone em segundo plano nem conteúdo de conversas."
          >
            <Toggle
              checked={devicePreferences.enabled === true}
              onChange={(enabled) => updateDeviceContextPreferences({ enabled })}
            />
          </SettingRow>
          <Hint>
            {deviceContextAvailable
              ? 'Você pode desligar a qualquer momento. A Athena só envia contexto quando uma ação sua pedir isso.'
              : deviceContext.available
                ? 'Este recurso está desativado por padrão e ainda não foi liberado nesta versão.'
                : 'Indisponível nesta PWA ou em uma versão Android anterior. Nenhum sinal é coletado.'}
          </Hint>
          <div style={{ opacity: deviceContextAvailable && devicePreferences.enabled ? 1 : .42, pointerEvents: deviceContextAvailable && devicePreferences.enabled ? 'auto' : 'none', marginTop: 8 }}>
            {[
              ['network', 'Rede', 'Conexão e tipo de rede, sem histórico de navegação.'],
              ['battery', 'Bateria', 'Nível aproximado e carregamento.'],
              ['audio', 'Saída de áudio', 'Categoria do dispositivo de saída; não grava som.'],
              ['appState', 'Estado do app', 'Em primeiro plano ou em segundo plano.'],
            ].map(([key, label, sub]) => (
              <SettingRow key={key} label={label} sub={sub}>
                <Toggle checked={devicePreferences[key] === true} onChange={(value) => updateDeviceContextPreferences({ [key]: value })} />
              </SettingRow>
            ))}
          </div>
          <div style={{ marginTop: 9, paddingTop: 9, borderTop: `1px solid ${P.border}` }}>
            <SettingRow label="Contexto de notificações" sub="Conteúdo de notificações nunca é compartilhado para contexto.">
              <span style={{ fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:20, background:'rgba(255,255,255,0.04)', color:P.dim }}>Indisponível</span>
            </SettingRow>
            <SettingRow label="Automações" sub="Não há execução automática de ações nesta versão.">
              <span style={{ fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:20, background:'rgba(255,255,255,0.04)', color:P.dim }}>Desativadas</span>
            </SettingRow>
          </div>
        </Box>

        {/* ── CÂMERA ── */}
        <SectionLabel>Câmera</SectionLabel>
        <Box>
          <SettingRow label="Câmera" sub="Chamadas visuais com a Athena e análise de imagem">
            <Badge st={camSt} />
          </SettingRow>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8 }}>
            {camSt !== 'granted' && camSt !== 'unsupported' && (
              <BtnPrim onClick={requestCam}>
                {camSt === 'loading' ? 'Solicitando…' : 'Solicitar permissão de câmera'}
              </BtnPrim>
            )}
            {camSt === 'granted' && !camActive && (
              <BtnGhost onClick={startCam}>Iniciar chamada visual com Athena</BtnGhost>
            )}
            {camActive && (
              <VideoPreview vRef={camVideoRef} onStop={stopCam} onFlip={flipCam} />
            )}
            {camSt === 'denied' && (
              <Hint>Para reativar, clique no ícone de câmera na barra de endereços do navegador.</Hint>
            )}
          </div>
        </Box>

        {/* ── LOCALIZAÇÃO ── */}
        <SectionLabel>Localização</SectionLabel>
        <Box>
          <SettingRow label="Geolocalização" sub="Contexto local, fuso horário e sugestões regionais">
            <Badge st={locSt} />
          </SettingRow>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8 }}>
            {locSt !== 'unsupported' && (
              <BtnGhost onClick={requestLoc} disabled={locSt === 'loading'}>
                {locSt === 'loading'  ? 'Obtendo localização…'     :
                 locSt === 'granted'  ? 'Atualizar localização agora' :
                                        'Solicitar permissão de localização'}
              </BtnGhost>
            )}
            {locData && (
              <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10,
                padding:'10px 12px', fontSize:11, color:P.muted, lineHeight:1.8 }}>
                <div>Latitude: <span style={{color:P.text}}>{locData.lat}</span></div>
                <div>Longitude: <span style={{color:P.text}}>{locData.lng}</span></div>
                <div>Precisão: <span style={{color:P.text}}>±{locData.acc} m</span></div>
                <div>Atualizado: <span style={{color:P.text}}>{locData.ts}</span></div>
              </div>
            )}
            {locSt === 'denied' && (
              <Hint>Permissão de localização bloqueada. Reative nas configurações do navegador.</Hint>
            )}
          </div>
        </Box>

        {/* ── TELA ── */}
        <SectionLabel>Tela</SectionLabel>
        <Box>
          <SettingRow label="Compartilhamento de tela" sub="Permite que Athena veja sua tela quando você autorizar">
            <Badge st={scrSt} />
          </SettingRow>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:8 }}>
            {scrSt !== 'unsupported' && !scrActive && (
              <BtnGhost onClick={requestScr}>Compartilhar tela com Athena</BtnGhost>
            )}
            {scrActive && (
              <VideoPreview vRef={scrVideoRef} onStop={stopScr} maxH={160} />
            )}
            <Hint>Athena nunca captura sua tela automaticamente. A sessão começa apenas com seu clique explícito.</Hint>
          </div>
        </Box>

        {/* ── OUTRAS PERMISSÕES ── */}
        <SectionLabel>Outras</SectionLabel>
        <Box>
          {[
            ['Microfone',         'Comandos de voz — autorizado pelo navegador ao clicar no mic'],
            ['Arquivos',          'Upload e leitura de documentos — solicitado ao fazer upload'],
            ['Memória persistente','Salvar e recordar informações — gerenciado pela Athena'],
          ].map(([label, sub]) => (
            <SettingRow key={label} label={label} sub={sub}>
              <span style={{ fontSize:10, color:P.dim }}>Nativo</span>
            </SettingRow>
          ))}
        </Box>

        {/* ── ACESSO AO DISPOSITIVO — FASE 5 ── */}
        <SectionLabel sub="Requer aplicativo nativo Android ou iOS">Acesso ao dispositivo</SectionLabel>
        <Box>
          {[
            'Android Assistant Mode',
            'Controle por voz global',
            'Acessibilidade do sistema',
            'Bluetooth / Fones de ouvido',
            'Controle de apps instalados',
            'Automações do dispositivo',
          ].map(label => (
            <SettingRow key={label} label={label} sub="Não disponível via navegador">
              <span style={{ fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:20,
                background:'rgba(255,255,255,0.04)', color:P.dim }}>Em breve</span>
            </SettingRow>
          ))}
        </Box>

      </div>
    </div>
  );
}

function ConfigScreen({ onBack, showToast, initPrefs, fontScale: initFontScale = 1.0, onFontScale = null }) {
  const _p = (() => { try { return JSON.parse(localStorage.getItem('athena_prefs')||'{}'); } catch { return {}; } })();
  const src = Object.keys(_p).length > 0 ? _p : (initPrefs || {});

  // Backward compat: sons was boolean, now string
  const sonsDefault = (() => {
    if (src.sons === true) return 'Sutil';
    if (typeof src.sons === 'string') return src.sons;
    return 'Desligado';
  })();

  const [intensidade,  setIntensidade]  = useState(src.intensidade  || 'Equilibrado');
  const [fonte,        setFonte]        = useState(src.fonte        || 'Inter');
  const [tamanhoFonte, setTamanhoFonte] = useState(src.tamanhoFonte || 'Normal');
  const [animacoes,    setAnimacoes]    = useState(src.animacoes    || 'Ativas');
  const [sons,         setSons]         = useState(sonsDefault);
  const [compacto,     setCompacto]     = useState(src.compacto     ?? false);
  const [wsScale,      setWsScale]      = useState(initFontScale);

  const handleWsScale = v => {
    setWsScale(v);
    onFontScale?.(v);
  };

  useEffect(() => {
    if (!initPrefs || Object.keys(initPrefs).length===0) return;
    if (localStorage.getItem('athena_prefs')) return;
    localStorage.setItem('athena_prefs', JSON.stringify(initPrefs));
  }, [initPrefs]);

  const fontMap = {
    'Inter':           'Inter, sans-serif',
    'Outfit':          '"Outfit", sans-serif',
    'Instrument Sans': '"Instrument Sans", sans-serif',
    'Serif':           'Georgia, serif',
  };
  const sizeMap = { 'Pequena':'13px', 'Normal':'15px', 'Grande':'17px' };

  const save = async () => {
    const prefs = { intensidade, fonte, tamanhoFonte, animacoes, sons, compacto };
    localStorage.setItem('athena_prefs', JSON.stringify(prefs));
    const existing = await apiGet(`${API_BASE}/auth/preferences`) || {};
    await apiPut(`${API_BASE}/auth/preferences`, { ...existing, ...prefs });
    applyPreferences(prefs);
    showToast('Configurações salvas e aplicadas.');
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Configurações do aplicativo" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'0 20px 32px'}}>

        {/* Intensidade Visual */}
        <SectionLabel>Intensidade Visual</SectionLabel>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {[
            ['Minimalista','Glow reduzido, sem partículas, interface limpa.'],
            ['Equilibrado', 'Configuração padrão — glow moderado, partículas normais.'],
            ['Imersivo',    'Glow completo, partículas ativas, atmosfera máxima.'],
          ].map(([opt,desc])=>(
            <button key={opt} onClick={()=>setIntensidade(opt)} style={{
              display:'flex',alignItems:'center',gap:12,padding:'11px 14px',
              borderRadius:12,border:`1px solid ${intensidade===opt?P.borderHi:P.border}`,
              background:intensidade===opt?P.cardHi:P.card,cursor:'pointer',textAlign:'left',transition:'all .15s',
            }}>
              <div style={{width:8,height:8,borderRadius:'50%',background:intensidade===opt?P.accent:'rgba(255,255,255,0.2)',flexShrink:0,transition:'background .15s'}}/>
              <div>
                <div style={{fontSize:13,fontWeight:500,color:intensidade===opt?P.text:'rgba(255,255,255,0.7)'}}>{opt}</div>
                <div style={{fontSize:11,color:P.dim,marginTop:1}}>{desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Fonte */}
        <SectionLabel>Fonte</SectionLabel>
        <Pill options={['Inter','Outfit','Instrument Sans','Serif']} value={fonte} onChange={setFonte}/>
        {/* Tamanho da Fonte */}
        <SectionLabel>Tamanho da Fonte</SectionLabel>
        <Pill options={['Pequena','Normal','Grande']} value={tamanhoFonte} onChange={setTamanhoFonte}/>
        <div style={{
          marginTop:8,padding:'14px 16px',borderRadius:14,
          background:'rgba(255,255,255,0.04)',border:`1px solid ${P.border}`,
          fontFamily:fontMap[fonte],fontSize:sizeMap[tamanhoFonte],
          color:P.text,lineHeight:1.6,transition:'all .25s',
        }}>
          <div style={{fontSize:'0.72em',color:P.dim,marginBottom:6,fontFamily:'Inter,sans-serif',letterSpacing:'.06em',textTransform:'uppercase'}}>Pré-visualização</div>
          A Athena é sua parceira estratégica. Ideias, decisões, execução — tudo junto com você.
        </div>

        {/* Texto no Workspace */}
        <SectionLabel sub="Escala o texto das respostas na tela do Workspace.">Texto no Workspace</SectionLabel>
        <div style={{background:'rgba(255,255,255,0.04)',borderRadius:12,padding:'14px 16px',border:`1px solid ${P.border}`}}>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
            <span style={{fontSize:11,color:P.muted,flexShrink:0,fontWeight:700,userSelect:'none'}}>A−</span>
            <input
              type="range" min={0.90} max={1.30} step={0.01}
              value={wsScale}
              onChange={e => handleWsScale(parseFloat(e.target.value))}
              style={{flex:1,accentColor:'#3B82F6',cursor:'pointer'}}
            />
            <span style={{fontSize:16,color:P.muted,flexShrink:0,fontWeight:700,userSelect:'none'}}>A+</span>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontSize:13,fontWeight:600,color:P.text}}>
              {wsScale <= 0.94 ? 'Compacto' : wsScale <= 1.04 ? 'Padrão' : wsScale <= 1.17 ? 'Confortável' : 'Amplo'}
            </span>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <span style={{fontSize:11,color:P.dim}}>{Math.round(wsScale * 100)}%</span>
              {Math.abs(wsScale - 1.0) > 0.005 && (
                <button onClick={() => handleWsScale(1.0)} style={{
                  fontSize:10,padding:'2px 8px',borderRadius:6,border:'none',
                  background:'rgba(59,130,246,0.18)',color:'#60a5fa',cursor:'pointer',
                }}>resetar</button>
              )}
            </div>
          </div>
          <div style={{
            marginTop:10,padding:'10px 12px',borderRadius:9,
            background:'rgba(255,255,255,0.03)',border:`1px solid rgba(255,255,255,0.06)`,
            fontSize:`calc(13px * ${wsScale})`,lineHeight:1.7,color:'rgba(232,234,242,0.75)',
          }}>
            Athena analisou o relatório e identificou pontos estratégicos para o próximo trimestre.
          </div>
        </div>

        {/* Animações */}
        <SectionLabel>Animações</SectionLabel>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {[
            ['Ativas',      'Fade, glow pulsante, partículas, transições completas.'],
            ['Reduzidas',   'Apenas transições suaves — sem partículas ou glow pulsante.'],
            ['Desativadas', 'Interface instantânea, sem nenhuma animação. Ideal para dispositivos mais fracos.'],
          ].map(([opt,desc])=>(
            <button key={opt} onClick={()=>setAnimacoes(opt)} style={{
              display:'flex',alignItems:'center',gap:12,padding:'11px 14px',
              borderRadius:12,border:`1px solid ${animacoes===opt?P.borderHi:P.border}`,
              background:animacoes===opt?P.cardHi:P.card,cursor:'pointer',textAlign:'left',transition:'all .15s',
            }}>
              <div style={{width:8,height:8,borderRadius:'50%',background:animacoes===opt?P.accent:'rgba(255,255,255,0.2)',flexShrink:0}}/>
              <div>
                <div style={{fontSize:13,fontWeight:500,color:animacoes===opt?P.text:'rgba(255,255,255,0.7)'}}>{opt}</div>
                <div style={{fontSize:11,color:P.dim,marginTop:1}}>{desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Feedback Sonoro */}
        <SectionLabel>Feedback Sonoro</SectionLabel>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {[
            ['Desligado','Nenhum som.'],
            ['Sutil',    'Som ao enviar mensagem e ao receber resposta.'],
            ['Imersivo', 'Sons para envio, resposta, painéis, voz e transições — discretos e elegantes.'],
          ].map(([opt,desc])=>(
            <button key={opt} onClick={()=>setSons(opt)} style={{
              display:'flex',alignItems:'center',gap:12,padding:'11px 14px',
              borderRadius:12,border:`1px solid ${sons===opt?P.borderHi:P.border}`,
              background:sons===opt?P.cardHi:P.card,cursor:'pointer',textAlign:'left',transition:'all .15s',
            }}>
              <div style={{width:8,height:8,borderRadius:'50%',background:sons===opt?P.accent:'rgba(255,255,255,0.2)',flexShrink:0}}/>
              <div>
                <div style={{fontSize:13,fontWeight:500,color:sons===opt?P.text:'rgba(255,255,255,0.7)'}}>{opt}</div>
                <div style={{fontSize:11,color:P.dim,marginTop:1}}>{desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Modo Compacto */}
        <SettingRow label="Modo Compacto" sub="Reduz espaçamentos; mensagens longas ficam recolhidas com opção de expandir">
          <Toggle checked={compacto} onChange={setCompacto}/>
        </SettingRow>

        {/* Idioma — Em breve */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 0',borderBottom:`1px solid ${P.border}`}}>
          <div>
            <div style={{fontSize:13,color:'rgba(255,255,255,0.3)'}}>Idioma</div>
            <div style={{fontSize:11,color:P.dim,marginTop:2}}>Múltiplos idiomas</div>
          </div>
          <span style={{fontSize:10,color:P.dim,background:'rgba(255,255,255,0.05)',border:`1px solid ${P.border}`,padding:'3px 10px',borderRadius:20,flexShrink:0}}>Em breve</span>
        </div>

        <div style={{display:'flex',gap:10,marginTop:22}}>
          <button onClick={save} style={{...btnPrimary,flex:1}}>Salvar e aplicar</button>
          <button onClick={async()=>{
            const def={intensidade:'Equilibrado',fonte:'Inter',tamanhoFonte:'Normal',animacoes:'Ativas',sons:'Desligado',compacto:false};
            setIntensidade(def.intensidade);
            setFonte(def.fonte);
            setTamanhoFonte(def.tamanhoFonte);
            setAnimacoes(def.animacoes);
            setSons(def.sons);
            setCompacto(def.compacto);
            localStorage.setItem('athena_prefs',JSON.stringify(def));
            applyPreferences(def);
            try{const ex=await apiGet(`${API_BASE}/auth/preferences`)||{};await apiPut(`${API_BASE}/auth/preferences`,{...ex,...def});}catch{}
            showToast('Padrão Athena restaurado ✓');
          }} style={btnGhost}>Restaurar padrão</button>
        </div>
      </div>
    </div>
  );
}

function ConectoresScreen({ onBack, showToast }) {
  const [email, setEmail] = useState('');
  const [espaco, setEspaco] = useState('');
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <ScreenHeader title="Conectores de colaboração" onBack={onBack}/>
      <div style={{flex:1,overflowY:'auto',padding:'0 20px 32px'}}>
        <div style={{marginTop:14,padding:'11px 14px',borderRadius:12,background:'rgba(59,130,246,0.06)',border:`1px solid ${P.borderHi}`,fontSize:12,color:P.accentMuted,lineHeight:1.5,marginBottom:6}}>Nenhum dado será compartilhado sem sua autorização explícita.</div>
        <SectionLabel>Convidar pessoa</SectionLabel>
        <div style={{display:'flex',gap:8}}>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email da pessoa" style={{...inputStyle,flex:1,fontSize:12}}/>
          <button onClick={()=>showToast('Em breve.')} style={{padding:'10px 16px',borderRadius:10,border:'none',background:P.accent,color:'#fff',fontSize:12,cursor:'pointer',flexShrink:0}}>Convidar</button>
        </div>
        <SectionLabel>Criar espaço colaborativo</SectionLabel>
        <div style={{display:'flex',gap:8}}>
          <input value={espaco} onChange={e=>setEspaco(e.target.value)} placeholder="Nome do espaço" style={{...inputStyle,flex:1,fontSize:12}}/>
          <button onClick={()=>showToast('Em breve.')} style={{padding:'10px 16px',borderRadius:10,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:12,cursor:'pointer',flexShrink:0}}>Criar</button>
        </div>
        <div style={{marginTop:20,padding:'12px 14px',borderRadius:12,background:'rgba(255,255,255,0.03)',border:`1px solid ${P.border}`,fontSize:12,color:P.dim,lineHeight:1.5}}>Chat colaborativo com Athena e compartilhamento de projetos — em breve.</div>
      </div>
    </div>
  );
}

// ── Tela principal + Modal ────────────────────────────────────────────────────

export function PanelPersonalizar({ onClose, user, onLogout, token, showToast: extToast, fontScale = 1.0, onFontScale }) {
  const [screen,     setScreen]     = useState('main');
  const [toastMsg,   setToastMsg]   = useState('');
  const [showLogout, setShowLogout] = useState(false);
  const [memCount,   setMemCount]   = useState(null);
  const [avatarUrl,  setAvatarUrl]  = useState(null);
  const [prefs,      setPrefs]      = useState({});
  const [uploading,  setUploading]  = useState(false);
  const fileRef = useRef(null);

  const username = user?.name  || 'Usuário';
  const email    = user?.email || '';
  const initials = username.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();

  const showToast = useCallback(msg => {
    if (extToast) { extToast(msg); return; }
    setToastMsg(msg); setTimeout(()=>setToastMsg(''),3200);
  }, [extToast]);

  useEffect(() => {
    const cached = localStorage.getItem('athena_prefs');
    if (cached) try { const p=JSON.parse(cached); setPrefs(p); applyPreferences(p); } catch {}

    apiGet('/auth/me').then(d=>{ if(d?.avatar) setAvatarUrl(d.avatar); });
    fetch(`${API_BASE}/memory/list`,{headers:authHeader()}).then(r=>r.json())
      .then(d=>setMemCount(Array.isArray(d.memories)?d.memories.length:null)).catch(()=>{});
    apiGet('/auth/preferences').then(p=>{ if(p&&Object.keys(p).length>0){setPrefs(p);applyPreferences(p);} });
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2*1024*1024) { showToast('Imagem muito grande. Máximo 2MB.'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const b64 = ev.target.result;
      setAvatarUrl(b64);
      await apiPut('/auth/me', {avatar:b64});
      showToast('Foto de perfil atualizada.'); setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const ITEMS = [
    {id:'perfil',  label:'Perfil',                     sub:'Dados que ajudam a Athena a conhecer você.',    d:'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                 badge:null,     hi:false},
    {id:'conta',   label:'Conta',                      sub:'Login, segurança e sessões.',                   d:'M17 21v-2a4 4 0 00-3-3.87M9 11a4 4 0 100-8 4 4 0 000 8zm-4 8v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75',                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                badge:null,     hi:false},
    {id:'memoria', label:'Memória',                    sub:memCount!=null?`${memCount} memórias salvas`:'O que Athena sabe e lembra sobre você.', d:'M12 2a7 7 0 015.29 11.55c-.58.69-1.29 1.27-1.29 2.2V17a2 2 0 01-2 2h-4a2 2 0 01-2-2v-1.25c0-.93-.71-1.51-1.29-2.2A7 7 0 0112 2zm-2 17h4',                                                                                                                                                                                                                                                                                                                                                                                                                                                           badge:memCount, hi:true},
    {id:'intel',   label:'Inteligência personalizada', sub:'Como Athena deve pensar, responder e interagir.',d:'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                        badge:null,     hi:true},
    {id:'apps',    label:'Aplicativos conectados',     sub:'3 serviços conectados',                          d:'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  badge:3,        hi:false},
    {id:'perms',   label:'Permissões',                 sub:'5 permissões ativas',                            d:'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4',                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                           badge:5,        hi:false},
    {id:'config',  label:'Configurações do aplicativo',sub:'Aparência, fonte, animações e comportamento.',  d:'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06-.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z', badge:null,     hi:false},
    {id:'collab',  label:'Conectores de colaboração',  sub:'IA e serviços externos.',                        d:'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm14 2l-4 4-2-2',                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                badge:null,     hi:false},
  ];

  const screenMap = {
    perfil:  <PerfilScreen       onBack={()=>setScreen('main')} showToast={showToast} userName={username}/>,
    conta:   <ContaScreen        onBack={()=>setScreen('main')} showToast={showToast}/>,
    memoria: <MemoriaScreen      onBack={()=>setScreen('main')} showToast={showToast} token={token}/>,
    intel:   <InteligenciaScreen onBack={()=>setScreen('main')} showToast={showToast} initPrefs={prefs}/>,
    apps:    <AplicativosScreen  onBack={()=>setScreen('main')} showToast={showToast}/>,
    perms:   <PermissoesScreen   onBack={()=>setScreen('main')} showToast={showToast}/>,
    config:  <ConfigScreen       onBack={()=>setScreen('main')} showToast={showToast} initPrefs={prefs} fontScale={fontScale} onFontScale={onFontScale}/>,
    collab:  <ConectoresScreen   onBack={()=>setScreen('main')} showToast={showToast}/>,
  };

  return (
    <>
      <style>{KF}</style>
      <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleFileChange}/>

      {/* Backdrop */}
      <div onClick={onClose} style={{position:'fixed',inset:0,zIndex:800,background:'rgba(5,10,25,0.30)',backdropFilter:'blur(22px)',WebkitBackdropFilter:'blur(22px)',animation:'bdIn .22s ease'}}/>

      {/* Modal */}
      <div style={{position:'fixed',inset:0,zIndex:801,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px 16px',boxSizing:'border-box',pointerEvents:'none'}}>
        <div style={{width:'100%',maxWidth:520,height:'90dvh',maxHeight:780,background:'rgba(8,12,28,0.30)',backdropFilter:'blur(32px)',WebkitBackdropFilter:'blur(32px)',border:'1px solid rgba(255,255,255,0.10)',borderRadius:28,boxShadow:'0 40px 100px rgba(0,0,0,0.55)',overflow:'hidden',display:'flex',flexDirection:'column',pointerEvents:'all',animation:'panelIn .22s cubic-bezier(.16,1,.3,1)',position:'relative'}}>

          <div style={{display:'flex',justifyContent:'center',paddingTop:12,flexShrink:0}}>
            <div style={{width:36,height:4,borderRadius:2,background:'rgba(255,255,255,0.15)'}}/>
          </div>

          <button onClick={onClose} style={{position:'absolute',top:14,right:14,zIndex:10,width:32,height:32,borderRadius:'50%',background:'rgba(255,255,255,0.10)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,0.6)',fontSize:18,transition:'background .15s'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.18)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.10)'}>×</button>

          <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
            {screen==='main' ? (
              <div style={{flex:1,overflowY:'auto'}}>
                {/* User card */}
                <div style={{margin:'8px 16px 0',padding:'32px 20px 28px',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:22,position:'relative',display:'flex',flexDirection:'column',alignItems:'center',textAlign:'center'}}>
                  <button onClick={()=>fileRef.current?.click()} disabled={uploading} title="Alterar foto" style={{position:'absolute',top:14,right:14,width:30,height:30,borderRadius:8,background:'rgba(255,255,255,0.08)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'background .15s',opacity:uploading?.5:1}}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.15)'}
                    onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
                    {uploading
                      ? <div style={{width:12,height:12,border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
                      : <Svg d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" size={13} color="rgba(255,255,255,0.5)"/>
                    }
                  </button>
                  <div style={{width:92,height:92,borderRadius:24,overflow:'hidden',background:'linear-gradient(135deg,#60a5fa 0%,#3B82F6 55%,#1d4ed8 100%)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32,fontWeight:700,color:'#fff',boxShadow:'0 0 0 3px rgba(59,130,246,0.20),0 12px 36px rgba(59,130,246,0.28)',marginBottom:18}}>
                    {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{width:'100%',height:'100%',objectFit:'cover'}}/> : initials}
                  </div>
                  <div style={{fontSize:22,fontWeight:700,color:'#fff',letterSpacing:'-.025em',lineHeight:1.2,marginBottom:8}}>{username}</div>
                  {email && <div style={{display:'flex',alignItems:'center',gap:5,color:'rgba(255,255,255,0.38)',fontSize:13}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {email}
                  </div>}
                </div>

                {/* Menu */}
                <div style={{padding:'16px 16px 28px',display:'flex',flexDirection:'column',gap:5}}>
                  {ITEMS.map(item=>(
                    <button key={item.id} onClick={()=>setScreen(item.id)} style={{display:'flex',alignItems:'center',gap:11,padding:'10px 13px',width:'100%',borderRadius:13,textAlign:'left',background:item.hi?P.cardHi:P.card,border:`1px solid ${item.hi?P.borderHi:P.border}`,cursor:'pointer',transition:'all .15s'}}
                      onMouseEnter={e=>e.currentTarget.style.background=item.hi?P.cardHiHov:P.cardHov}
                      onMouseLeave={e=>e.currentTarget.style.background=item.hi?P.cardHi:P.card}>
                      <div style={{width:34,height:34,borderRadius:10,flexShrink:0,background:item.hi?'rgba(59,130,246,0.18)':'rgba(59,130,246,0.11)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <Svg d={item.d} size={16}/>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:500,color:P.text}}>{item.label}</div>
                        <div style={{fontSize:11,color:P.muted,marginTop:1}}>{item.sub}</div>
                      </div>
                      {item.badge!=null&&<span style={{fontSize:10,fontWeight:600,color:P.accent,background:'rgba(59,130,246,0.14)',padding:'2px 8px',borderRadius:20,flexShrink:0}}>{item.badge}</span>}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                  ))}
                  <button onClick={()=>setShowLogout(true)} style={{display:'flex',alignItems:'center',gap:11,padding:'10px 13px',width:'100%',borderRadius:13,textAlign:'left',background:P.redCard,border:`1px solid ${P.redBorder}`,cursor:'pointer',transition:'all .15s',marginTop:4}}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(239,68,68,0.11)'}
                    onMouseLeave={e=>e.currentTarget.style.background=P.redCard}>
                    <div style={{width:34,height:34,borderRadius:10,flexShrink:0,background:'rgba(239,68,68,0.13)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      <Svg d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" size={16} color={P.red}/>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:500,color:P.red}}>Sair da conta</div>
                      <div style={{fontSize:11,color:'rgba(239,68,68,0.45)',marginTop:1}}>Encerrar sessão da Athena</div>
                    </div>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(239,68,68,0.28)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                </div>
              </div>
            ) : (
              <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>{screenMap[screen]}</div>
            )}
          </div>
        </div>
      </div>

      {toastMsg && <div style={{position:'fixed',bottom:28,left:'50%',zIndex:820,padding:'10px 18px',borderRadius:12,background:'rgba(12,18,38,0.96)',border:'1px solid rgba(255,255,255,0.1)',boxShadow:'0 8px 32px rgba(0,0,0,0.55)',fontSize:13,color:P.text,backdropFilter:'blur(14px)',WebkitBackdropFilter:'blur(14px)',whiteSpace:'nowrap',animation:'toastIn .18s ease'}}>{toastMsg}</div>}

      {showLogout && (
        <div style={{position:'fixed',inset:0,zIndex:820,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 20px',background:'rgba(0,0,0,0.20)',backdropFilter:'blur(4px)'}}>
          <div style={{width:'100%',maxWidth:360,padding:'24px',background:'rgba(12,18,38,0.92)',backdropFilter:'blur(20px)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:20,boxShadow:'0 40px 100px rgba(0,0,0,0.8)',animation:'panelIn .2s cubic-bezier(.16,1,.3,1)'}}>
            <div style={{fontSize:17,fontWeight:700,color:'#fff',marginBottom:8}}>Sair da Athena?</div>
            <div style={{fontSize:13,color:P.muted,lineHeight:1.65,marginBottom:22}}>Você será desconectado desta sessão. Suas memórias e conversas continuarão salvas.</div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setShowLogout(false)} style={{flex:1,padding:'12px',borderRadius:12,border:`1px solid ${P.border}`,background:'none',color:P.muted,fontSize:13,cursor:'pointer'}}>Cancelar</button>
              <button onClick={()=>{setShowLogout(false);onLogout();}} style={{flex:1,padding:'12px',borderRadius:12,border:'none',background:P.red,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer'}}>Sair da conta</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
